/**
 * FeedPanel — the EXTERNAL world surface (Reddit / RSS / YouTube / Bluesky).
 *
 * Deliberately DISTINCT from TimelinePanel (internal SAGE/client data). High
 * density, Reddit-is-Fun style: compact rows, score + comment counts, source
 * chips, relative time, inline source toggles. All live via the Rust http_get
 * command — never seed data (the CSV journal calls out the old seed-item sin).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, ArrowUp, MessageSquare, ExternalLink, Rss, Monitor, AlertTriangle, SlidersHorizontal } from "lucide-react";
import { PanelScroll } from "./ClientsPanel";
import { loadFeed, getSources, setSources, relTime, type FeedItem, type FeedSource, type SourceResult } from "../lib/feed";
import { setChatContext } from "../lib/chatContext";

const KIND_TINT: Record<string, string> = {
  reddit: "#d49a6a", // wheat/orange
  rss: "#7ba3c9",
  youtube: "#cc5a5a",
  bluesky: "#6a9fd4",
};

export default function FeedPanel() {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [perSource, setPerSource] = useState<SourceResult[]>([]);
  const [supported, setSupported] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showSources, setShowSources] = useState(false);
  const [sources, setSourcesState] = useState<FeedSource[]>(() => getSources());

  const refresh = useCallback(async () => {
    setLoading(true);
    const res = await loadFeed();
    setItems(res.items);
    setPerSource(res.perSource);
    setSupported(res.supported);
    setLoading(false);
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const toggleSource = (id: string) => {
    const next = sources.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s));
    setSourcesState(next);
    setSources(next);
  };

  const errs = useMemo(() => perSource.filter((p) => p.error), [perSource]);

  return (
    <PanelScroll accentVar="--biome-business" title="Feed" subtitle="external world · Reddit · RSS · live">
      {/* controls */}
      <div className="flex items-center gap-2 -mt-1">
        <button onClick={() => void refresh()} className="flex items-center gap-1.5 px-3 h-8 rounded-full text-body-sm border bg-surface text-text-muted hover:text-text hover:border-primary/40 transition-colors">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <button onClick={() => setShowSources((v) => !v)} className="flex items-center gap-1.5 px-3 h-8 rounded-full text-body-sm border bg-surface text-text-muted hover:text-text hover:border-primary/40 transition-colors">
          <SlidersHorizontal size={13} /> Sources ({sources.filter((s) => s.enabled).length})
        </button>
        {!loading && supported && <span className="text-caption text-text-dim ml-1">{items.length} items</span>}
      </div>

      {showSources && (
        <div className="flex flex-wrap gap-1.5 p-3 rounded-xl border bg-surface">
          {sources.map((s) => (
            <button key={s.id} onClick={() => toggleSource(s.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-caption font-medium border transition-colors ${s.enabled ? "text-text" : "text-text-dim opacity-60"}`}
              style={s.enabled ? { borderColor: KIND_TINT[s.kind], backgroundColor: `color-mix(in srgb, ${KIND_TINT[s.kind]} 16%, transparent)`, color: KIND_TINT[s.kind] } : { borderColor: "var(--border)" }}>
              <Rss size={11} /> {s.label}
            </button>
          ))}
        </div>
      )}

      {!supported ? (
        <div className="flex items-center gap-2 text-body-sm text-text-dim py-6 px-1">
          <Monitor size={16} /> <span>The live external feed requires the desktop app (browser can't fetch Reddit/RSS).</span>
        </div>
      ) : loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="skeleton h-12 rounded-lg" />)}
        </div>
      ) : (
        <>
          {errs.length > 0 && (
            <div className="flex items-start gap-2 text-caption text-warning px-1">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>{errs.map((e) => `${e.source.label}: ${e.error}`).join(" · ")}</span>
            </div>
          )}
          {items.length === 0 ? (
            <div className="text-body-sm text-text-dim py-6 px-1">No items — enable a source or refresh.</div>
          ) : (
            <div className="rounded-xl border bg-surface divide-y divide-border overflow-hidden">
              {items.map((it) => <FeedRow key={it.id} it={it} />)}
            </div>
          )}
        </>
      )}
    </PanelScroll>
  );
}

function FeedRow({ it }: { it: FeedItem }) {
  const tint = KIND_TINT[it.kind] ?? "var(--text-muted)";
  // Tap the row → pull into the central chat (framework). The ↗ icon opens the
  // original externally without hijacking the row.
  const discuss = () => setChatContext({ label: `${it.sourceLabel}: ${it.title.slice(0, 48)}`, detail: `${it.title}\nSource: ${it.sourceLabel}${it.author ? ` · ${it.author}` : ""}\n${it.url}${it.summary ? `\n\n${it.summary}` : ""}` });
  return (
    <div onClick={discuss} className="group flex gap-3 px-3 py-2 hover:bg-surface-hover transition-colors items-start cursor-pointer">
      {it.score != null && (
        <div className="flex flex-col items-center w-9 shrink-0 pt-0.5">
          <ArrowUp size={12} className="text-text-dim" />
          <span className="font-mono text-caption text-text-muted">{compact(it.score)}</span>
        </div>
      )}
      {it.thumbnail && (
        <img src={it.thumbnail} alt="" className="w-12 h-12 rounded-md object-cover shrink-0 border border-border" loading="lazy" />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-body-sm text-text font-medium leading-snug line-clamp-2 group-hover:text-primary transition-colors">{it.title}</div>
        <div className="flex items-center gap-2 flex-wrap mt-1 text-caption text-text-dim">
          <span className="px-1.5 py-0.5 rounded-full font-medium" style={{ color: tint, backgroundColor: `color-mix(in srgb, ${tint} 16%, transparent)` }}>{it.sourceLabel}</span>
          {it.author && <span>{it.author}</span>}
          {it.ts > 0 && <span className="font-mono">{relTime(it.ts)}</span>}
          {it.comments != null && <span className="flex items-center gap-0.5"><MessageSquare size={10} /> {compact(it.comments)}</span>}
        </div>
      </div>
      {it.url && (
        <a href={it.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Open original"
          className="shrink-0 p-1 text-text-dim hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"><ExternalLink size={13} /></a>
      )}
    </div>
  );
}

function compact(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}
